import GasStation from '../entities/GasStation.js';
import Coin from '../entities/Coin.js';
import {
    COIN_SYSTEM,
    GAS_STATIONS,
    ENVIRONMENT
} from '../constants/gameSettings.js';

export default class CollectibleManager {
    constructor(scene, physics, terrainHeightMap) {
        this.scene = scene;
        this.physics = physics;
        this.terrainHeightMap = terrainHeightMap;

        // Dependencies set after construction
        this.bus = null;
        this.audioManager = null;
        this.scoreManager = null;
        this.uiManager = null;

        // Coin system state
        this.coins = [];
        this.coinCount = COIN_SYSTEM.COUNT;
        this.coinSpawnRadius = COIN_SYSTEM.SPAWN_RADIUS;
        this.coinMinDistance = COIN_SYSTEM.MIN_DISTANCE;
        this.refuelCost = COIN_SYSTEM.REFUEL_COST;

        // Gas station state
        this.gasStations = [];
        this.gasStationCount = GAS_STATIONS.COUNT;

        // Event handler references for cleanup
        this.onCoinCollected = null;
        this.onCoinsUpdated = null;
    }

    setBus(bus) { this.bus = bus; }
    setAudioManager(audioManager) { this.audioManager = audioManager; }
    setScoreManager(scoreManager) { this.scoreManager = scoreManager; }
    setUIManager(uiManager) { this.uiManager = uiManager; }

    getGasStations() { return this.gasStations; }
    getCoins() { return this.coins; }
    getRefuelCost() { return this.refuelCost; }

    initialize() {
        this.addGasStations();
    }

    initializeCoins() {
        if (!this.physics || !this.physics.isInitialized) {
            console.error("Cannot add coins - physics not initialized!");
            return;
        }
        this.addCoins();
    }

    update(deltaTime) {
        this.updateGasStations(deltaTime);
        this.updateCoins(deltaTime);
        this.updateContextualActionPrompt();
    }

    clear() {
        // Remove event listeners
        if (this.onCoinCollected) {
            window.removeEventListener('coin_collected', this.onCoinCollected);
        }
        if (this.onCoinsUpdated) {
            window.removeEventListener('coins_updated', this.onCoinsUpdated);
        }

        // Clean up gas stations
        if (this.gasStations && this.gasStations.length) {
            this.gasStations.forEach(station => station?.remove());
            this.gasStations = [];
        }

        // Clean up coins
        if (this.coins && this.coins.length) {
            this.coins.forEach(coin => coin?.remove());
            this.coins = [];
        }
    }

    // --- Gas Stations ---

    addGasStations() {
        // Clear any existing gas stations
        if (this.gasStations) {
            this.gasStations.forEach(station => station.remove());
            this.gasStations = [];
        }

        const stations = [];
        const terrainSize = ENVIRONMENT.TERRAIN_SIZE / 4;
        const minDistance = GAS_STATIONS.MIN_DISTANCE;

        for (let i = 0; i < this.gasStationCount; i++) {
            let position;
            let validPosition = false;
            let attempts = 0;

            while (!validPosition && attempts < 50) {
                position = this.getValidPosition(terrainSize, []);

                validPosition = true;
                for (const existingStation of stations) {
                    const distance = Math.sqrt(
                        Math.pow(existingStation.position.x - position.x, 2) +
                        Math.pow(existingStation.position.z - position.z, 2)
                    );

                    if (distance < minDistance) {
                        validPosition = false;
                        break;
                    }
                }

                attempts++;
            }

            if (validPosition) {
                const rotation = Math.floor(Math.random() * 4) * (Math.PI / 2);
                const station = new GasStation(this.scene, this.physics, {
                    x: position.x,
                    z: position.z,
                    rotation: rotation
                });
                stations.push(station);
            }
        }

        this.gasStations = stations;
    }

    updateGasStations(deltaTime) {
        if (!this.gasStations || !this.bus) return;
        for (const station of this.gasStations) {
            station.update(deltaTime, this.bus);
        }
    }

    // --- Coins ---

    addCoins() {
        this.#clearExistingCoins();

        if (!this.physics || !this.physics.isInitialized) {
            console.error("Cannot add coins - physics not initialized!");
            return;
        }

        const coins = [];
        const coinPositions = [];

        for (let i = 0; i < this.coinCount; i++) {
            const position = this.#findValidCoinPosition(coinPositions);
            if (!position) continue;

            try {
                const coin = new Coin(this.scene, this.physics, position);
                coins.push(coin);
                coinPositions.push(position);
            } catch (error) {
                console.error("Error creating coin:", error);
            }
        }

        this.coins = coins;
        this.setupCoinCollectionHandler();
        console.log(`Created ${coins.length} coins`);
    }

    updateCoins(deltaTime) {
        if (!this.coins || this.coins.length === 0) return;

        this.coins = this.coins.filter(coin => coin && !coin.collected);

        for (const coin of this.coins) {
            if (coin) coin.update(deltaTime);
        }

        if (this.coins.length < this.coinCount * 0.2) {
            this.addMoreCoins(Math.min(10, this.coinCount * 0.1));
        }

        this.updateCoinsDisplay();
    }

    addMoreCoins(count) {
        const coinPositions = this.coins.map(coin => coin.position);

        for (let i = 0; i < count; i++) {
            let position;
            let validPosition = false;
            let attempts = 0;

            while (!validPosition && attempts < 50) {
                const angle = Math.random() * Math.PI * 2;
                const radius = Math.random() * COIN_SYSTEM.SPAWN_RADIUS;

                const mcX = Math.cos(angle) * radius;
                const mcZ = Math.sin(angle) * radius;
                const mcTerrainY = this.terrainHeightMap ? this.terrainHeightMap.getHeight(mcX, mcZ) : 0;
                position = {
                    x: mcX,
                    y: mcTerrainY + 1.5,
                    z: mcZ
                };

                validPosition = true;
                for (const existingPosition of coinPositions) {
                    const distance = Math.sqrt(
                        Math.pow(existingPosition.x - position.x, 2) +
                        Math.pow(existingPosition.z - position.z, 2)
                    );
                    if (distance < COIN_SYSTEM.MIN_DISTANCE) {
                        validPosition = false;
                        break;
                    }
                }

                attempts++;
            }

            if (validPosition) {
                const coin = new Coin(this.scene, this.physics, position);
                this.coins.push(coin);
                coinPositions.push(position);
            }
        }
    }

    #clearExistingCoins() {
        if (this.coins && this.coins.length > 0) {
            this.coins.forEach(coin => {
                if (coin && !coin.collected) coin.remove();
            });
            this.coins = [];
        }
    }

    #findValidCoinPosition(existingPositions) {
        const maxAttempts = 50;
        let attempts = 0;

        while (attempts < maxAttempts) {
            const angle = Math.random() * Math.PI * 2;
            const radius = Math.random() * this.coinSpawnRadius;

            const coinX = Math.cos(angle) * radius;
            const coinZ = Math.sin(angle) * radius;
            const coinTerrainY = this.terrainHeightMap ? this.terrainHeightMap.getHeight(coinX, coinZ) : 0;
            const position = {
                x: coinX,
                y: coinTerrainY + 1.5,
                z: coinZ
            };

            if (this.#isValidCoinPosition(position, existingPositions)) {
                return position;
            }
            attempts++;
        }
        return null;
    }

    #isValidCoinPosition(position, existingPositions) {
        for (const existingPosition of existingPositions) {
            const distance = Math.sqrt(
                Math.pow(existingPosition.x - position.x, 2) +
                Math.pow(existingPosition.z - position.z, 2)
            );
            if (distance < this.coinMinDistance) return false;
        }
        return true;
    }

    // --- Event Handlers ---

    setupCoinCollectionHandler() {
        // Remove existing listeners to prevent duplicates
        window.removeEventListener('coin_collected', this.onCoinCollected);
        window.removeEventListener('coins_updated', this.onCoinsUpdated);

        this.onCoinCollected = (event) => {
            if (event && event.detail) {
                this.updateCoinsDisplay();

                if (this.audioManager) {
                    this.audioManager.playSoundEffect('coin_collected');
                }

                const coinValue = event.detail.value || 1;

                if (!this.bus.coins) this.bus.coins = 0;
                this.bus.coins += coinValue;

                this.showNotification(`+${coinValue} COIN!`, "rgba(175, 206, 90, 0.0)");

                if (this.scoreManager) {
                    this.scoreManager.addScore('coinCollected');
                }
            } else {
                console.warn("Received coin_collected event but detail is missing:", event);
            }
        };

        this.onCoinsUpdated = (event) => {
            if (event && event.detail) {
                if (this.bus && typeof event.detail.coins === 'number') {
                    this.bus.coins = event.detail.coins;
                }

                this.updateCoinsDisplay();

                if (this.audioManager) {
                    this.audioManager.playSoundEffect('coin_collected');
                }

                if (event.detail.operation === 'refuel') {
                    const refuelCost = event.detail.cost || this.refuelCost || 1;
                    this.showNotification(`-${refuelCost} COIN!`, "rgba(255, 100, 0, 0.8)");
                } else {
                    const coinValue = event.detail.coin ? event.detail.coin.value || 1 : 1;
                    this.showNotification(`+${coinValue} COIN!`, "rgba(255, 215, 0, 0.8)");
                }
            } else {
                console.warn("Received coins_updated event but detail is missing:", event);
            }
        };

        window.addEventListener('coin_collected', this.onCoinCollected);
        window.addEventListener('coins_updated', this.onCoinsUpdated);
    }

    // --- UI Helpers ---

    updateCoinsDisplay() {
        const coinCount = this.bus ? (this.bus.coins || 0) : 0;
        if (this.uiManager) {
            this.uiManager.updateCoinsDisplay(coinCount);
        }
    }

    updateContextualActionPrompt() {
        if (!this.uiManager || !this.bus || !this.gasStations) return;

        let nearbyStation = null;
        for (const station of this.gasStations) {
            if (station && typeof station.checkBusProximity === 'function' && station.checkBusProximity(this.bus)) {
                nearbyStation = station;
                break;
            }
        }

        if (nearbyStation) {
            this.uiManager.setActionPrompt(`Refueling zone: ${nearbyStation.name}`, 'success');
        } else {
            this.uiManager.setActionPrompt('', 'neutral');
        }
    }

    showNotification(message, backgroundColor) {
        if (this.uiManager) {
            this.uiManager.showNotificationMessage(message, backgroundColor);
        }
    }

    // --- Position Helpers ---

    getValidPosition(terrainSize, exclusionZones) {
        const halfSize = terrainSize / 2;
        let isValid = false;
        let x, z;

        let attempts = 0;
        while (!isValid && attempts < 50) {
            attempts++;
            x = Math.random() * terrainSize - halfSize;
            z = Math.random() * terrainSize - halfSize;

            isValid = true;

            for (const zone of exclusionZones) {
                const relX = x - zone.position.x;
                const relZ = z - zone.position.z;
                const rotatedX = relX * Math.cos(-zone.rotation) - relZ * Math.sin(-zone.rotation);
                const rotatedZ = relX * Math.sin(-zone.rotation) + relZ * Math.cos(-zone.rotation);

                if (
                    Math.abs(rotatedX) < zone.width / 2 &&
                    Math.abs(rotatedZ) < zone.length / 2
                ) {
                    isValid = false;
                    break;
                }
            }
        }

        return { x, z };
    }
}
